import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { RootState } from '../../../states/containers/store'
import { destroyTaskProjectTemplateSetting, fetchTaskReportLabelGroup } from '../../../states/containers/task/taskSlice'
import { LangValue } from '../../../states/types/common'
import { getGetPrams, setPath, setUrlId } from '../../../utils/url'
import { URL } from '../../constants/url'
import { diffDays, endOfMonth, format, nowDate, secToHour, secToMinute, startOfMonth } from '../../../utils/date'
import LoadingSquare from '../../components/common/LoadingSquare'
import SearchConditionModule from '../../components/task/SearchConditionModule'
import { RequestGetTaskReportLabelGroup } from '../../../states/types/task'
import COLOR_LIST from '../../constants/taskColor'
import CreateProjectModal from '../../components/task/CreateProjectModal'
import VideoGuide from '../../components/common/VideoGuide'
import DateModule from '../../components/task/MonthCalendarModule'
import ImportProjectModal from '../../components/task/ImportProjectModal'
import GroupButton from '../../components/common/GroupButton'
import { useAlert } from 'react-alert'
import ConfirmationModal from '../../components/task/ConfirmationModal'
import { updateTaskSearchQuery } from '../../../states/containers/common/currentSlice'

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  description?: string
  permission: {
    viewable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: {
        id: number
        name: string
      }[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

interface GetParams {
  team_ids: string
  member_ids: string
  keywords: string
  disabled: string
  key: string
  direction: string
  colors: string
  creator_member_ids: string
  viewer_member_ids: string
  user_member_ids: string
  date: string
}

interface Data {
  id: number
  name: string
  code: string | null
  color: string
  scheduled_time: number | null
  schedule: {
    start_date: string
    scheduled_end_date: string | null
    actual_end_date: string | null
  }
  time: {
    scheduled: number | null
    actual: number
  }
  cost: {
    scheduled: number | null
    actual: number
    other: number
  }
}

const Index: FC = () => {
  const {
    task,
    login,
    commonPart,
    current,
  } = useSelector((state: RootState) => state)

  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const DISPLAY_LIMIT = 10

  const [pageOffset, setPageOffset] = useState<number>(1)
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const [keywords, setKeywords] = useState<string[]>([])
  const [key, setKey] = useState<string>("scheduled_start_date")
  const [direction, setDirection] = useState<string>("desc")
  const [isFirst, setIsFirst] = useState<boolean>(true)

  const [colorLabels, setColorLabels] = useState<string[]>([])
  const [creatorMemberIds, setCreatorMemberIds] = useState<number[]>([])
  const [viewerMemberIds, setViewerMemberIds] = useState<number[]>([])
  const [userMemberIds, setUserMemberIds] = useState<number[]>([])

  const [data, setData] = useState<Data[]>([])

  const [isDataLoad, setIsDataLoad] = useState<boolean>(false)

  const [isLoadingFirst, setIsLoadingFirst] = useState<boolean>(true)
  const [isLoadingList, setIsLoadingList] = useState<boolean>(false)

  const [isStop, setIsStop] = useState<boolean>(false)
  const stopRef = useRef(isStop)

  const [loadTrigger, setLoadTrigger] = useState<boolean>(false)

  const [isOpenCreateProjectModal, setIsOpenCreateProjectModal] = useState<boolean>(false)
  const [isDisplayAllDate, setIsDisplayAllDate] = useState<boolean>(true)
  const [date, setDate] = useState<string>(nowDate("YYYY-MM-01"))

  const [isOpenImportModal, setIsOpenImportModal] = useState<boolean>(false)

  const [fetchTrigger, setFetchTrigger] = useState<string>("")

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const tableHeight = useMemo(() => {
    if ((task.taskReportLabelGroup?.page_count || 0) <= 1) {
      return 99 * (task.taskReportLabelGroup?.label_groups.length || 1)
    }
    return 99 * DISPLAY_LIMIT
  }, [task.taskReportLabelGroup?.page_count])

  const clickDetail = (id: number) => {
    if (!isDisplayAllDate) {
      window.open(`${setPath(setUrlId(URL.TASK_DETAIL_DETAIL.path, 'id', id), language)}?date=${date}`)
      return
    }
    window.open(`${setPath(setUrlId(URL.TASK_DETAIL_DETAIL.path, 'id', id), language)}`)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clickCreator = (state: {detail?: TemplateDetail | null, labelGroupId?: number}) => {
    history.push({
      pathname: `${setPath(URL.TASK_LABEL_SETTING_CREATE.path, language)}`,
      state: state,
    })
  }

  const createProject = () => {
    setIsOpenCreateProjectModal(true)
  }

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (!stopRef.current && !isLoadingList && (bottomHeight() * adjustment) < (currentScroll() + windowHeight())) {
      setIsDataLoad(true)
    }
  }, [stopRef])

  const clickImport = () => {
    setIsOpenImportModal(true)
    setIsOpenCreateProjectModal(false)
  }

  const addImport = () => {
    alert.success("プロジェクト一括登録を受け付けました")
    setFetchTrigger(uniqueId())
  }

  const openConfirmationModal = (id: number, name: string) => {
    const callback = () => {
      dispatch(
        destroyTaskProjectTemplateSetting(
          { id: id.toString() },
          () => {
            alert.success("ユーザーテンプレートを削除しました！")
          },
        ),
      )
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: "delete",
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    if (isLoadingFirst || isStop || !isDataLoad) return
    setPageOffset(pageOffset + 1)
    setIsLoadingList(true)
  }, [isDataLoad])

  useEffect(() => {
    if (!task.taskReportLabelGroup?.label_groups.length) return
    setData([...data, ...task.taskReportLabelGroup.label_groups])
  }, [task.taskReportLabelGroup?.label_groups])

  useEffect(() => {
    let getParams = getGetPrams(location.search) as GetParams
    if (!location.search && current.taskSearchQuery) {
      getParams = getGetPrams(current.taskSearchQuery) as GetParams
    }

    if (getParams.disabled) {
      setIsDisabled(JSON.parse(getParams.disabled.toLowerCase()))
    }
    if (getParams.keywords) {
      setKeywords(decodeURIComponent(getParams.keywords).split(','))
    }
    if (getParams.key) {
      if (getParams.key === "order_index" && login.permissions?.high_volume) {
        setKey("id")
      } else {
        setKey(getParams.key)
      }
    }
    if (getParams.direction) {
      if (getParams.key === "order_index" && login.permissions?.high_volume) {
        setDirection("desc")
      } else {
        setDirection(getParams.direction)
      }
    }
    if (getParams.colors) {
      setColorLabels(getParams.colors.split(','))
    }
    if (getParams.creator_member_ids) {
      setCreatorMemberIds(getParams.creator_member_ids.split(',').map((v) => Number(v)))
    }
    if (getParams.viewer_member_ids) {
      setViewerMemberIds(getParams.viewer_member_ids.split(',').map((v) => Number(v)))
    }
    if (getParams.user_member_ids) {
      setUserMemberIds(getParams.user_member_ids.split(',').map((v) => Number(v)))
    }
    if (getParams.date) {
      setDate(getParams.date)
      setIsDisplayAllDate(false)
    }
    setLoadTrigger(true)
  }, [])

  useEffect(() => {
    if (isFirst) {
      setIsFirst(false)
      return
    }
    const query = [`disabled=${isDisabled}`]
    const params: RequestGetTaskReportLabelGroup = {
      page: 1,
      limit: DISPLAY_LIMIT,
      disabled: isDisabled,
    }
    setPageOffset(1)
    setData([])
    setIsStop(false)
    setIsLoadingFirst(true)
    if (keywords.length) {
      params.keywords = keywords
      query.push(`keywords=${keywords}`)
    }
    if (key) {
      params.key = key
      query.push(`key=${key}`)
    }
    if (direction) {
      params.direction = direction
      query.push(`direction=${direction}`)
    }
    if (colorLabels.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => colorLabels.includes(v.label)).map((v) => v.color)
      query.push(`colors=${colorLabels}`)
    }
    if (creatorMemberIds.length) {
      params.creator_member_ids = creatorMemberIds
      query.push(`creator_member_ids=${creatorMemberIds}`)
    }
    if (viewerMemberIds.length) {
      params.viewer_member_ids = viewerMemberIds
      query.push(`viewer_member_ids=${viewerMemberIds}`)
    }
    if (userMemberIds.length) {
      params.user_member_ids = userMemberIds
      query.push(`user_member_ids=${userMemberIds}`)
    }
    if (!isDisplayAllDate) {
      params.start_date = startOfMonth(date, "YYYY-MM-DD")
      params.end_date = endOfMonth(date, "YYYY-MM-DD")
      query.push(`date=${date}`)
    }
    dispatch(fetchTaskReportLabelGroup(
      params,
      (result) => {
        setIsLoadingFirst(false)
        setIsDataLoad(false)
        if (!result.label_groups.length) {
          setIsStop(true)
        }
      },
    ))
    history.push(`${setPath(URL.TASK.path, language)}?${query.join('&')}`)
  // eslint-disable-next-line max-len
  }, [keywords, isDisabled, key, direction, loadTrigger, colorLabels, creatorMemberIds, viewerMemberIds, userMemberIds, isDisplayAllDate, date, fetchTrigger])

  useEffect(() => {
    if (pageOffset === 1) {
      setIsDataLoad(false)
      setIsLoadingList(false)
      return
    }
    const params: RequestGetTaskReportLabelGroup = {
      page: pageOffset,
      limit: DISPLAY_LIMIT,
      disabled: isDisabled,
    }
    if (keywords.length) {
      params.keywords = keywords
    }
    if (key) {
      params.key = key
    }
    if (direction) {
      params.direction = direction
    }
    if (colorLabels.length) {
      params.colors = COLOR_LIST.map((v) => v.values).flat().filter((v) => colorLabels.includes(v.label)).map((v) => v.color)
    }
    if (creatorMemberIds.length) {
      params.creator_member_ids = creatorMemberIds
    }
    if (viewerMemberIds.length) {
      params.viewer_member_ids = viewerMemberIds
    }
    if (userMemberIds.length) {
      params.user_member_ids = userMemberIds
    }
    if (!isDisplayAllDate) {
      params.start_date = startOfMonth(date, "YYYY-MM-DD")
      params.end_date = endOfMonth(date, "YYYY-MM-DD")
    }
    dispatch(fetchTaskReportLabelGroup(
      params,
      (result) => {
        setIsDataLoad(false)
        setIsLoadingList(false)
        if (!result.label_groups.length) {
          setIsStop(true)
        }
      },
    ))
  }, [pageOffset])

  useEffect(() => {
    if (!location.search) return
    dispatch(updateTaskSearchQuery(location.search))
  }, [location.search])

  useEffect(() => {
    stopRef.current = isStop
  }, [isStop])

  return (
    <section className="task_page">
      <Helmet>
        <title>Qasee - 工数管理 - プロジェクトレポート</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        プロジェクトレポート
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          プロジェクトレポート
          <VideoGuide
            videoId="9WCY6RzOEck"
          />
        </h1>
        <div className="tools">
          {
            login.permissions?.task_project_setting && (
              <div className="button_area">
                <GroupButton
                  list={[
                    {
                      icon: "plus",
                      text: "プロジェクトを新規作成する",
                      callback: createProject,
                    },
                    {
                      icon: "plus",
                      text: "プロジェクトを一括登録する",
                      callback: () => setIsOpenImportModal(true),
                    },
                  ]}
                />
              </div>
            )
          }
        </div>
      </section>
      <section className="page_description">
        <p>
          プロジェクトごとにコスト・時間・業務割合を可視化します。
        </p>
      </section>
      <SearchConditionModule
        name="プロジェクト一覧"
        keywords={keywords}
        sortKey={key}
        sortDirection={direction}
        disabled={isDisabled}
        colors={colorLabels}
        creatorMemberIds={creatorMemberIds}
        viewerMemberIds={viewerMemberIds}
        userMemberIds={userMemberIds}
        memberList={task.taskReportLabelGroup?.members.map((v) => ({ id: v.id, name: v.name }))}
        callbackKeywords={setKeywords}
        callbackKey={setKey}
        callbackDirection={setDirection}
        callbackDisabled={setIsDisabled}
        callbackPageOffset={setPageOffset}
        callbackColorLabels={setColorLabels}
        callbackCreatorMemberIds={setCreatorMemberIds}
        callbackViewerMemberIds={setViewerMemberIds}
        callbackUserMemberIds={setUserMemberIds}
      />
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="title">
              現在のプロジェクト状況
            </div>
            <div>
              {
                !!task.taskReportLabelGroup?.threshold_time && (
                  <>
                    {`${format(task.taskReportLabelGroup?.threshold_time, "YYYY年M月D日HH時mm分ss秒")}時点の集計データを表示しています`}
                  </>
                )
              }
            </div>
          </div>
          <div className="block-header">
            <div />
            <div className="tools">
              <button
                type="button"
                className={`display_switch ${isDisplayAllDate ? "check" : ""}`}
                onClick={() => setIsDisplayAllDate(!isDisplayAllDate)}
              >
                <div>
                  <FontAwesomeIcon icon={['far', 'calendar-alt']} />
                  <span>
                    全期間
                  </span>
                </div>
                <div>
                  <FontAwesomeIcon icon={['far', 'calendar-check']} />
                  <span>
                    月別
                  </span>
                </div>
              </button>
              <DateModule
                date={date}
                startMonth={format(commonPart.company?.created_at, "YYYYMM")}
                changeCallback={(callback_date) => {
                  setDate(callback_date)
                  history.push(`${location.pathname}?date=${callback_date}`)
                }}
                disabled={isDisplayAllDate}
              />
            </div>
          </div>
          <div className="block-detail">
            {
              isLoadingFirst ? (
                <div
                  style={{
                    minHeight: `${tableHeight + 31.8}px`,
                  }}
                >
                  <LoadingSquare>
                    <>
                      現在のプロジェクト状況を生成しています…
                    </>
                  </LoadingSquare>
                </div>
              ) : (
                <>
                  {
                    data.length ? (
                      <div className="summary_area_2">
                        <table className={`${isDisabled ? "completed" : ""}`}>
                          <thead>
                            <tr>
                              <th> </th>
                              <th>
                                スケジュール
                              </th>
                              <th>
                                作業時間
                              </th>
                              {
                                login.permissions?.task_cost_view && (
                                  <th>
                                    損益
                                    <span className="cost_view">
                                      コスト表示権限を持つユーザーのみ表示されています
                                    </span>
                                  </th>
                                )
                              }
                            </tr>
                          </thead>
                        </table>
                        {
                          data.map((list) => (
                            <table key={uniqueId()} className={`${isDisabled ? " completed" : ""}`}>
                              <tbody>
                                <tr>
                                  <td rowSpan={2} className="reverse">
                                    <div className="project_title">
                                      <div
                                        style={{
                                          backgroundColor: list.color,
                                          minWidth: "12px",
                                          height: "12px",
                                          border: "1px solid #FFFFFF",
                                        }}
                                      />
                                      <div
                                        style={{
                                          wordBreak: "break-all",
                                        }}
                                      >
                                        {list.name}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => clickDetail(list.id)}
                                      style={{
                                        color: "#4372ba",
                                      }}
                                    >
                                      詳細を見る
                                      <FontAwesomeIcon icon={['fas', 'external-link-alt']} className="icon" />
                                    </button>
                                  </td>
                                  <td>
                                    終了予定日
                                  </td>
                                  <td>
                                    予定
                                  </td>
                                  <td>
                                    実績
                                  </td>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <>
                                        <td>
                                          売上・予算
                                        </td>
                                        <td>
                                          業務コスト
                                        </td>
                                        <td>
                                          その他のコスト
                                        </td>
                                        <td>
                                          利益
                                        </td>
                                      </>
                                    )
                                  }
                                </tr>
                                <tr style={{ marginBottom: "16px" }}>
                                  <td>
                                    {
                                      list.schedule.actual_end_date ? (
                                        <div className="completed">
                                          完了
                                        </div>
                                      ) : (
                                        <>
                                          {
                                            list.schedule.scheduled_end_date ? (
                                              <>
                                                {format(list.schedule.scheduled_end_date)}
                                                <div className={`limit${diffDays(nowDate(), list.schedule.scheduled_end_date) < 0 ? " red" : ""}`}>
                                                  (
                                                  {diffDays(nowDate(), list.schedule.scheduled_end_date) > 0 && "あと"}
                                                  <span>
                                                    {Math.abs(diffDays(nowDate(), list.schedule.scheduled_end_date))}
                                                  </span>
                                                  日
                                                  {diffDays(nowDate(), list.schedule.scheduled_end_date) < 0 && "超過"}
                                                  )
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                -
                                              </>
                                            )
                                          }
                                        </>
                                      )
                                    }
                                  </td>
                                  <td>
                                    <div className="value" style={{ fontSize: "10px" }}>
                                      {
                                        list.time.scheduled ? (
                                          <>
                                            {
                                              Number(secToHour(list.time.scheduled)) > 0
                                                ? (
                                                  <>
                                                    <span style={{ fontSize: "14px" }}>
                                                      {secToHour(list.time.scheduled)}
                                                    </span>
                                                    時間
                                                  </>
                                                )
                                                : ""
                                            }
                                            {
                                              Number(secToMinute(list.time.scheduled)) > 0
                                                ? (
                                                  <>
                                                    <span style={{ fontSize: "14px" }}>
                                                      {secToMinute(list.time.scheduled)}
                                                    </span>
                                                    分
                                                  </>
                                                )
                                                : ""
                                            }
                                          </>
                                        ) : (
                                          "-"
                                        )
                                      }
                                    </div>
                                  </td>
                                  <td>
                                    <div
                                      className="value"
                                      style={{ fontSize: "10px" }}
                                    >
                                      {
                                        list.time.actual ? (
                                          <>
                                            {
                                              Number(secToHour(list.time.actual)) > 0
                                                ? (
                                                  <>
                                                    <span style={{ fontSize: "14px" }}>
                                                      {secToHour(list.time.actual)}
                                                    </span>
                                                    時間
                                                  </>
                                                )
                                                : ""
                                            }
                                            {
                                              Number(secToMinute(list.time.actual)) > 0
                                                ? (
                                                  <>
                                                    <span style={{ fontSize: "14px" }}>
                                                      {secToMinute(list.time.actual)}
                                                    </span>
                                                    分
                                                  </>
                                                )
                                                : ""
                                            }
                                          </>
                                        ) : (
                                          "-"
                                        )
                                      }
                                    </div>
                                  </td>
                                  {
                                    login.permissions?.task_cost_view && (
                                      <>
                                        <td>
                                          <div className={`value${(list.cost.scheduled || 0) < 100000000 ? "" : " small"}`}>
                                            {list.cost.scheduled ? `¥${list.cost.scheduled.toLocaleString()}` : "-"}
                                          </div>
                                        </td>
                                        <td>
                                          <div
                                            className={
                                              `value${list.cost.actual < 100000000 ? "" : " small"}`
                                            }
                                          >
                                            {`¥${list.cost.actual.toLocaleString()}`}
                                          </div>
                                        </td>
                                        <td>
                                          <div
                                            className={
                                              `value${(list.cost.other || 0) < 100000000 ? "" : " small"}`
                                            }
                                          >
                                            {list.cost.other ? `¥${list.cost.other.toLocaleString()}` : "-"}
                                          </div>
                                        </td>
                                        <td
                                          // eslint-disable-next-line max-len
                                          className={`${list.cost.scheduled && list.cost.scheduled - (list.cost.actual + list.cost.other) <= 0 ? "zero" : ""}`}
                                        >
                                          <div>
                                            {
                                              list.cost.scheduled ? (
                                                <>
                                                  {
                                                    (((list.cost.scheduled - (list.cost.actual + list.cost.other)) / list.cost.scheduled) * 100) >= 0
                                                      // eslint-disable-next-line max-len
                                                      ? `${(((list.cost.scheduled - (list.cost.actual + list.cost.other)) / list.cost.scheduled) * 100).toFixed(1)}%`
                                                      : "0%"
                                                  }
                                                  <div className="add_area">
                                                    {`(¥${(list.cost.scheduled - (list.cost.actual + list.cost.other)).toLocaleString()})`}
                                                  </div>
                                                </>
                                              ) : (
                                                "-"
                                              )
                                            }
                                          </div>
                                        </td>
                                      </>
                                    )
                                  }
                                </tr>
                              </tbody>
                            </table>
                          ))
                        }
                      </div>
                    ) : (
                      <div className="summary_no_data">
                        データがありません
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
          {
            isLoadingList && (
              <div className="block-detail">
                <LoadingSquare>
                  <>
                    現在のプロジェクト状況を生成しています…
                  </>
                </LoadingSquare>
              </div>
            )
          }
        </div>
      </section>
      <CreateProjectModal
        closeCallback={() => setIsOpenCreateProjectModal(false)}
        addCallback={(state) => clickCreator(state)}
        deleteCallback={(id, name) => openConfirmationModal(id, name)}
        importCallback={clickImport}
        isDisplay={isOpenCreateProjectModal}
      />
      <ImportProjectModal
        isDisplay={isOpenImportModal}
        dataList={task.taskLabelGroupImport}
        closeCallback={() => setIsOpenImportModal(false)}
        addCallback={() => addImport()}
        isCost={!!login.permissions?.task_project_cost_setting}
      />
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
    </section>
  )
}

export default Index
